import React, { useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  Baby, LayoutDashboard, Bed, Users, Stethoscope, ClipboardList, Search, Settings, LogOut,
  Sun, Moon, Activity, PlusCircle, ChevronLeft, Home, ChevronRight, ChevronDown, UserRound, RefreshCw, Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import OfflineIndicator from "@/components/OfflineIndicator";
import GlobalAlerts from "@/components/GlobalAlerts";
import TrainingBanner from "@/components/TrainingBanner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Admin", icon: Settings },
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/beds", label: "Bed Map", icon: Bed },
    { to: "/patients", label: "Patients", icon: Users },
    { to: "/rounds", label: "Doctor Rounds", icon: Stethoscope },
    { to: "/nursing", label: "Nursing", icon: ClipboardList },
    { to: "/billing", label: "Billing", icon: Receipt },
  ],
  receptionist: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/beds", label: "Bed Map", icon: Bed },
    { to: "/patients", label: "Patients", icon: Users },
    { to: "/billing", label: "Billing", icon: Receipt },
  ],
  receptionist: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/beds", label: "Bed Map", icon: Bed },
    { to: "/patients", label: "Patients", icon: Users },
    { to: "/admit", label: "Admit Patient", icon: PlusCircle },
  ],
  doctor: [
    { to: "/rounds", label: "Rounds", icon: Stethoscope },
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/beds", label: "Bed Map", icon: Bed },
    { to: "/patients", label: "Patients", icon: Users },
  ],
  nurse: [
    { to: "/nursing", label: "My Patients", icon: ClipboardList },
    { to: "/beds", label: "Bed Map", icon: Bed },
    { to: "/admit", label: "Admit Patient", icon: PlusCircle },
  ],
};

const ROUTE_LABELS = {
  admin: "Admin",
  dashboard: "Dashboard",
  beds: "Bed Map",
  patients: "Patients",
  admit: "Admit",
  nursing: "Nursing",
  rounds: "Rounds",
};

function useBreadcrumb() {
  const { pathname } = useLocation();
  return useMemo(() => {
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length === 0) return [];
    const crumbs = [];
    let path = "";
    for (let i = 0; i < parts.length; i++) {
      path += "/" + parts[i];
      const prev = parts[i - 1];
      let label;
      if (parts[i - 1] === "patients" && parts[i]) label = "Detail"; // /patients/:id
      else label = ROUTE_LABELS[parts[i]] || decodeURIComponent(parts[i]);
      crumbs.push({ path, label });
    }
    return crumbs;
  }, [pathname]);
}

function homeFor(role) {
  if (role === "admin") return "/admin";
  if (role === "nurse") return "/nursing";
  if (role === "doctor") return "/rounds";
  return "/dashboard";
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const crumbs = useBreadcrumb();
  const items = NAV_BY_ROLE[user?.role] || [];
  const home = homeFor(user?.role);
  const canGoBack = loc.pathname !== home && (window.history.length > 1);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[264px_1fr]">
      {/* Sidebar */}
      <aside className="border-r border-border bg-card/70 backdrop-blur hidden lg:flex flex-col">
        <Link to={home} className="px-6 py-6 flex items-center gap-3 group" data-testid="sidebar-brand">
          <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center text-primary group-hover:bg-primary/20 transition-colors">
            <Baby size={20} />
          </div>
          <div>
            <div className="font-display font-semibold leading-tight">NICU Suite</div>
            <div className="text-xs text-muted-foreground">Neonatal EMR</div>
          </div>
        </Link>

        <nav className="px-3 pt-2 space-y-1 flex-1">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `sidenav-link ${isActive ? "active" : ""}`}
                     data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} end>
              <Icon size={18} />
              <span className="text-sm">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center text-primary text-sm font-semibold">
              {(user?.full_name || user?.username || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name || user?.username}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
            </div>
            <button className="text-muted-foreground hover:text-red-500 transition-colors" onClick={() => { logout(); nav("/login", { replace: true }); }} data-testid="btn-logout" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col min-w-0">
        <TrainingBanner />
        <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {/* Mobile brand */}
              <Link to={home} className="lg:hidden h-9 w-9 rounded-xl bg-primary/10 grid place-items-center text-primary" title="Home"><Baby size={18} /></Link>

              {/* Back / Home */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => nav(-1)}
                  disabled={!canGoBack}
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="btn-back"
                  title="Back"
                >
                  <ChevronLeft size={16} />
                </button>
                <Link
                  to={home}
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary"
                  data-testid="btn-home"
                  title="Home"
                >
                  <Home size={16} />
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className="h-9 w-9 grid place-items-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary"
                  data-testid="btn-refresh"
                  title="Refresh page"
                >
                  <RefreshCw size={15} />
                </button>
              </div>

              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-1 text-sm min-w-0" data-testid="breadcrumb">
                <Link to={home} className="text-muted-foreground hover:text-foreground">Home</Link>
                {crumbs.map((c, i) => (
                  <React.Fragment key={c.path}>
                    <ChevronRight size={12} className="text-muted-foreground" />
                    {i === crumbs.length - 1 ? (
                      <span className="font-medium truncate max-w-[240px]">{c.label}</span>
                    ) : (
                      <Link to={c.path} className="text-muted-foreground hover:text-foreground truncate max-w-[160px]">{c.label}</Link>
                    )}
                  </React.Fragment>
                ))}
              </nav>

              <div className="hidden xl:flex items-center gap-2 text-xs text-muted-foreground pl-3 ml-2 border-l border-border">
                <Activity size={12} className="text-primary" /> Live
                <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <OfflineIndicator />
              <button data-testid="quick-search-btn"
                      onClick={() => nav("/patients")}
                      className="hidden md:flex items-center gap-2 h-9 rounded-full border border-border px-3 text-sm text-muted-foreground hover:text-foreground">
                <Search size={14} /> Search patients…
              </button>
              <button data-testid="theme-toggle" onClick={toggle}
                      className="h-9 w-9 rounded-full border border-border grid place-items-center text-muted-foreground hover:text-foreground" title="Toggle theme">
                {dark ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="h-9 inline-flex items-center gap-2 rounded-full border border-border pl-1 pr-3 text-sm hover:border-primary hover:text-primary transition-colors"
                  >
                    <span className="h-7 w-7 rounded-full bg-primary/10 grid place-items-center text-primary text-xs font-semibold">
                      {(user?.full_name || user?.username || "U").slice(0, 1).toUpperCase()}
                    </span>
                    <span className="hidden md:inline max-w-[140px] truncate">{user?.full_name || user?.username}</span>
                    <ChevronDown size={14} className="text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user?.full_name || user?.username}</div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{user?.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={home} className="cursor-pointer"><Home size={14} className="mr-2" /> Home</Link>
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer"><Settings size={14} className="mr-2" /> Admin settings</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => { logout(); nav("/login", { replace: true }); }}
                    data-testid="menu-signout"
                    className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 cursor-pointer"
                  >
                    <LogOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile role nav */}
          <div className="lg:hidden overflow-x-auto no-scrollbar flex gap-1 px-4 pb-2">
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} end
                       className={({ isActive }) => `whitespace-nowrap text-xs px-3 py-1.5 rounded-full border transition-colors ${isActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <Icon size={12} className="inline mr-1" />{label}
              </NavLink>
            ))}
          </div>
        </header>

        <GlobalAlerts />

        <main className="p-4 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
